import React, { memo, useContext } from 'react'
import { AnyStyledComponent } from 'styled-components'
import { Header, ScreenContext } from 'react-components'

import {
    GhostButtonContent,
    GhostButtonWrapper,
    HeaderBar,
    HeaderBarContent,
    IconImage,
    LeftSubContainer,
    MenuIconWrapper,
    RightSubContainer,
    SearchIconContainer,
    StickyContainer,
    SeoHref,
} from './header-section-styles'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import MenuDrawer from './menu-drawer'
import ProfileDrawer from './profile-drawer'
import { useLandingPageHeader } from '../hooks'
import { sanitizeUrlPath } from '../utils'
import { MegabarTransport } from '../../../transports/common/megabar-transport'
import GhostButton from '../../common/ghost-button'
import SearchIcon from '../../../assets/icons/userbar-icons/search-grey.svg'
import NavigationIcon from '../../../assets/icons/userbar-icons/navigation-grey.svg'
import LogoArea from './logo-area'
import SearchArea from './search-area'
import NavigationLinks from './navigation-links'
import WishlistButton from './wishlist-button'
import UserMenuDesktop from './user-menu-desktop'
import UserMenuMobile from './user-menu-mobile'

interface Props {
    isLoggedIn: boolean
    firstName?: string
    url: string
    headerSection: HeaderSectionTransport
    sitePathname?: string
    showVacationIdeasNavButton: boolean
    onSearchIconClick?: React.MouseEventHandler<HTMLDivElement>
    isAllInputModalOpen?: boolean
    hideListRental?: boolean
    searchRegionNode?: any
    showSearchbar?: boolean
    showNavigationLinks?: boolean
    megabar?: MegabarTransport
}

const HeaderSection: React.FC<Props> = ({
    isLoggedIn,
    firstName,
    url,
    headerSection,
    sitePathname = '',
    showVacationIdeasNavButton = true,
    isAllInputModalOpen,
    hideListRental = false,
    searchRegionNode,
    showSearchbar = false,
    showNavigationLinks = true,
    megabar,
    onSearchIconClick,
}) => {
    const { isDesktop, isMobile, isTablet } = useContext(ScreenContext)
    const navigation = headerSection.navigationSectionResponse.navigation
    const { listRentalButton, logo, wishlistButton } = navigation
    const listRentalButtonText = listRentalButton.text

    const {
        isSidebarOpen,
        toggleSidebar,
        isUserSidebarOpen,
        toggleUserSidebar,
        popUpBoxVisible,
        togglePopUpBoxVisibility,
        listRentalClickHandler,
        wishlistClickHandler,
        wishlistCount,
        isLandlordRole,
        isGuestRole,
        inquiryCount,
    } = useLandingPageHeader({ url, listRentalButton })

    return (
        <StickyContainer>
            <Header id="header">
                <HeaderBar>
                    <HeaderBarContent>
                        <LeftSubContainer>
                            <LogoArea logo={logo} />
                            <SearchArea
                                isMobile={isMobile}
                                searchRegionNode={searchRegionNode}
                                showSearchbar={showSearchbar}
                                isAllInputModalOpen={isAllInputModalOpen}
                                onSearchIconClick={onSearchIconClick}
                            />
                            <NavigationLinks
                                navigation={navigation}
                                showVacationIdeasNavButton={showVacationIdeasNavButton}
                                isMobile={isMobile}
                                isTablet={isTablet}
                                showNavigationLinks={showNavigationLinks}
                            />
                        </LeftSubContainer>
                        <RightSubContainer>
                            {(isMobile || isTablet) && !isLoggedIn && !hideListRental && !!listRentalButton?.url && (
                                <GhostButtonWrapper>
                                    <SeoHref
                                        href={sanitizeUrlPath(listRentalButton.url)}
                                        noCrawl={listRentalButton.noCrawl ?? false}
                                    >
                                        <GhostButton onClick={listRentalClickHandler} padding={'0.3125rem'} hoverEffect>
                                            <GhostButtonContent>{listRentalButton.text}</GhostButtonContent>
                                        </GhostButton>
                                    </SeoHref>
                                </GhostButtonWrapper>
                            )}
                            <WishlistButton
                                wishlistButton={wishlistButton}
                                wishlistCount={wishlistCount}
                                onClick={wishlistClickHandler}
                            />
                            {!!onSearchIconClick && isMobile && (
                                <SearchIconContainer onClick={onSearchIconClick}>
                                    <IconImage as={SearchIcon as AnyStyledComponent} />
                                </SearchIconContainer>
                            )}
                            {isMobile || isTablet ? (
                                <UserMenuMobile
                                    navigation={navigation}
                                    isLoggedIn={isLoggedIn}
                                    sitePathname={sitePathname}
                                    toggleUserSidebar={toggleUserSidebar}
                                />
                            ) : (
                                <UserMenuDesktop
                                    navigation={navigation}
                                    isLoggedIn={isLoggedIn}
                                    firstName={firstName}
                                    sitePathname={sitePathname}
                                    isLandlordRole={isLandlordRole}
                                    isGuestRole={isGuestRole}
                                    inquiryCount={inquiryCount}
                                    popUpBoxVisible={popUpBoxVisible}
                                    togglePopUpBoxVisibility={togglePopUpBoxVisibility}
                                />
                            )}
                            {(isMobile || isTablet) && (
                                <MenuIconWrapper onClick={toggleSidebar}>
                                    <IconImage as={NavigationIcon as AnyStyledComponent} />
                                </MenuIconWrapper>
                            )}
                            {!isMobile &&
                                !isTablet &&
                                !!listRentalButton?.url &&
                                (isLoggedIn ? !isLandlordRole : true) && (
                                    <GhostButtonWrapper>
                                        <SeoHref
                                            href={sanitizeUrlPath(listRentalButton.url)}
                                            noCrawl={listRentalButton.noCrawl ?? false}
                                        >
                                            <GhostButton
                                                onClick={listRentalClickHandler}
                                                padding={'0.3125rem'}
                                                hoverEffect
                                            >
                                                <GhostButtonContent>{listRentalButton.text}</GhostButtonContent>
                                            </GhostButton>
                                        </SeoHref>
                                    </GhostButtonWrapper>
                                )}
                        </RightSubContainer>
                    </HeaderBarContent>
                </HeaderBar>
                {(isMobile || isTablet) && (
                    <MenuDrawer
                        isLoggedIn={isLoggedIn}
                        open={isSidebarOpen && !isDesktop}
                        onClose={toggleSidebar}
                        headerSection={headerSection}
                        listRentalClickHandler={listRentalClickHandler}
                        showVacationIdeasNavButton={showVacationIdeasNavButton}
                        isLandlordRole={isLandlordRole}
                        isGuestRole={isGuestRole}
                        megabar={megabar}
                    />
                )}
                {(isMobile || isTablet) && isLoggedIn && (
                    <ProfileDrawer
                        open={isUserSidebarOpen && !isDesktop}
                        onClose={toggleUserSidebar}
                        headerSection={headerSection}
                        isLandlordRole={isLandlordRole}
                        isGuestRole={isGuestRole}
                        firstName={firstName}
                        inquiryCount={inquiryCount}
                    />
                )}
            </Header>
        </StickyContainer>
    )
}

export default memo(HeaderSection)
